import { useEffect, useId, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocalizedPath } from "../../i18n/localizedPath";
import styles from "./similarKanjiQuiz.module.scss";

interface SummaryProps {
    results: { groupId: string; correct: boolean }[];
    signedOut: boolean;
    saved: boolean;
    onRestart: () => void;
}

export default function SimilarKanjiQuizSummary({ results, signedOut, saved, onRestart }: Readonly<SummaryProps>) {
    const { t } = useTranslation("similarKanjiPage");
    const lp = useLocalizedPath();
    const headingRef = useRef<HTMLHeadingElement>(null);
    const scoreId = useId();
    const correct = results.filter((result) => result.correct).length;
    const wrong = results.length - correct;
    const percent = results.length > 0 ? Math.round((correct / results.length) * 100) : 0;
    const missed = Array.from(new Set(results.filter((result) => !result.correct).map((result) => result.groupId)));

    // The question card had focus; hand it to the summary so it is announced.
    useEffect(() => {
        headingRef.current?.focus();
    }, []);

    return <div className={styles.quizCard}>
        <h2 ref={headingRef} tabIndex={-1} aria-describedby={scoreId} className={styles.summaryTitle}>{t("quiz.summary.title")}</h2>
        <div id={scoreId} className={styles.scoreBlock}>
            <p className={styles.percent}>{t("quiz.summary.percent", { percent })}</p>
            <p>{t("quiz.summary.score", { correct, wrong })}</p>
        </div>

        <div className={styles.missed}>
            <h3>{t("quiz.summary.missedTitle")}</h3>
            {missed.length === 0
                ? <p>{t("quiz.summary.missedNone")}</p>
                : <ul>
                    {missed.map((groupId) => <li key={groupId}>
                        <strong lang="ja">{t(`groups.${groupId}.title`)}</strong>
                        <span>{t(`groups.${groupId}.difference`)}</span>
                    </li>)}
                </ul>}
        </div>

        {signedOut && <p className={styles.note}>
            <Link to={lp("/login")}>{t("quiz.summary.loginPrompt.login")}</Link>{" "}
            {t("quiz.summary.loginPrompt.or")}{" "}
            <Link to={lp("/register")}>{t("quiz.summary.loginPrompt.register")}</Link>{" "}
            {t("quiz.summary.loginPrompt.suffix")}
        </p>}
        {!signedOut && saved && <p className={styles.note}>{t("quiz.summary.saved")}</p>}

        <div className={styles.actions}>
            <button type="button" className={styles.primaryButton} onClick={onRestart}>{t("quiz.summary.tryAgain")}</button>
            <Link to={lp("/similar-kanji")} className={styles.secondaryButton}>{t("quiz.summary.backToGuide")}</Link>
        </div>
    </div>;
}
